'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Button, Modal, message } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';

import { useAppDispatch } from '@/hooks/useRedux';
import { performSignOut } from '@/redux/auth/signin/performSignOut';
import { beginLogout, endLogout } from '@/utils/session';
import { prefixPath } from '@/utils/prefixPath';
import { redirectTo } from '@/utils/navigation';

const SIGN_IN_PATH = '/account/signin';
const HARD_REDIRECT_AFTER_MS = 8000;

type LogoutContextValue = {
  requestLogout: () => void;
  loggingOut: boolean;
};

const LogoutContext = createContext<LogoutContextValue | null>(null);

export const useLogout = (): LogoutContextValue => {
  const ctx = useContext(LogoutContext);
  if (!ctx) throw new Error('useLogout must be used within <LogoutProvider>');
  return ctx;
};

export const LogoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const [messageApi, messageHolder] = message.useMessage();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const requestLogout = useCallback(() => {
    if (!busy) setOpen(true);
  }, [busy]);

  const cancel = useCallback(() => {
    if (!busy) setOpen(false);
  }, [busy]);

  const confirm = useCallback(() => {
    if (busy) return;

    setBusy(true);
    beginLogout();
    performSignOut()(dispatch);
    router.replace(SIGN_IN_PATH);

    fallbackRef.current = setTimeout(() => {
      redirectTo(prefixPath(`${SIGN_IN_PATH}/`));
    }, HARD_REDIRECT_AFTER_MS);
  }, [busy, dispatch, router]);

  useEffect(() => {
    if (!busy || !pathname?.includes(SIGN_IN_PATH)) return;

    if (fallbackRef.current) clearTimeout(fallbackRef.current);
    endLogout();
    setBusy(false);
    setOpen(false);
    messageApi.success({ content: 'You have been logged out.', key: 'logout' });
  }, [busy, pathname, messageApi]);

  useEffect(
    () => () => {
      if (fallbackRef.current) clearTimeout(fallbackRef.current);
    },
    []
  );

  const value = useMemo(
    () => ({ requestLogout, loggingOut: busy }),
    [requestLogout, busy]
  );

  return (
    <LogoutContext.Provider value={value}>
      {children}
      {messageHolder}

      <Modal
        open={open}
        centered
        width={400}
        destroyOnHidden
        closable={!busy}
        maskClosable={!busy}
        keyboard={!busy}
        onCancel={cancel}
        title={
          <span className="flex items-center gap-2">
            <LogoutOutlined aria-hidden="true" />
            Log out?
          </span>
        }
        footer={[
          <Button key="cancel" onClick={cancel} disabled={busy}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            autoFocus
            loading={busy}
            onClick={confirm}
          >
            {busy ? 'Logging out…' : 'Log out'}
          </Button>,
        ]}
      >
        <p className="text-sm text-gray-600">
          You will be signed out on this device and taken back to the sign-in
          page. Sign in again to see your invoices, receipts and applications.
        </p>
      </Modal>
    </LogoutContext.Provider>
  );
};
